// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  AlertIcon,
  Banner,
  Box,
  Button,
  Flex,
  Link,
  Skeleton,
} from '@iheartjane/reefer';
import { Table } from '@iheartjane/reefer-table';
import type {
  ReactTable,
  RowData,
  RowSelectionState,
} from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback } from 'react';
import { useInView } from 'react-intersection-observer';

import { useProductSearchFilters } from '@jane/business-admin/data-access';
import { useStoreId } from '@jane/business-admin/hooks';
import { useProductsTable } from '@jane/business-admin/providers/ProductsTableProvider';
import { EventNames, track } from '@jane/business-admin/util';

import { CLARIFICATION_LEARN_MORE_URL } from './ClarificationCell';
import { EmptyState } from './EmptyStates';
import type { ProductTableFeatures } from './productTableFeatures';
import { TableFilter } from './TableFilter';

import styles from './baseProductTable.module.css';

const ROW_HEIGHT = '72';
const StyledRow = styled(Table.Row)(
  ({ useDefaultCursor }: { useDefaultCursor?: boolean }) => ({
    height: `${ROW_HEIGHT}px`,
    cursor: useDefaultCursor ? 'default' : 'pointer',
  })
);

const reduceAndSortFilterData = (
  productData: string[],
  filterData: string[]
) => {
  return productData
    .reduce(
      (acc: string[], curr: string | null) =>
        curr === null || acc.includes(curr) || curr === ''
          ? acc
          : [...acc, curr],
      [...filterData]
    )
    .filter(Boolean)
    .sort((a: string, b: string) => a.localeCompare(b));
};

const LoadingRow = <TData extends RowData>({
  index = 0,
  table,
}: {
  index?: number;
  table: ReactTable<ProductTableFeatures, TData>;
}) => (
  <StyledRow key={`${index}-row-loading`}>
    {table.getAllColumns().map((column) => (
      <Table.Cell key={`${column.id}-loading-cell`}>
        <Skeleton animate>
          <Skeleton.Bone />
        </Skeleton>
      </Table.Cell>
    ))}
  </StyledRow>
);

export const BaseProductTable = <TData extends RowData>({
  allIds,
  columnFiltersParent,
  fetchNextPage,
  handleRowClick,
  hasNextPage,
  isBulkEditTable,
  isFetched,
  isFetching,
  productData,
  table,
  tableType,
}: {
  allIds: RowSelectionState;
  columnFiltersParent?: any;
  fetchNextPage: () => void;
  handleRowClick: (
    row: any,
    e: ReactMouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void;
  hasNextPage: boolean;
  isBulkEditTable: boolean;
  isFetched: boolean;
  isFetching: boolean;
  productData: any;
  table: ReactTable<ProductTableFeatures, TData>;
  tableType: 'published' | 'hidden' | 'unpublished';
}) => {
  const { canEditProducts } = useProductsTable();
  const bulkEditInvalidProducts = useFlag(FLAGS.c1BulkEditInvalidProducts);

  const storeId = useStoreId();
  const { ref: loadNextRef } = useInView({
    onChange: (inView) => {
      if (inView) {
        fetchNextPage();
      }
    },
    rootMargin: '300px',
  });

  const findFilter = useCallback(
    (filterId: string) => {
      const found = columnFiltersParent?.find(
        ({ id }: { id: string }) => id === filterId
      );
      if (found) {
        return found.value;
      }
      return undefined;
    },
    [columnFiltersParent]
  );

  const { data: filterData, isFetching: filtersFetching } =
    useProductSearchFilters({
      category: findFilter('category'),
      storeId,
      subcategory: findFilter('subcategory'),
    });

  const filters = {
    name: [''],
    brand:
      !filtersFetching && isFetched && filterData
        ? reduceAndSortFilterData(
            productData.map((product: any) => product.brand),
            filterData?.filters?.brands
          )
        : [],
    brand_subtype:
      !filtersFetching && isFetched && filterData
        ? reduceAndSortFilterData(
            productData.map((product: any) => product.brand_subtype),
            filterData?.filters?.brand_subtypes
          )
        : [],
    category:
      !filtersFetching && isFetched && filterData
        ? reduceAndSortFilterData(
            productData.map((product: any) => product.category),
            filterData?.filters?.categories
          )
        : [],
    subcategory:
      !filtersFetching && isFetched && filterData
        ? reduceAndSortFilterData(
            productData.map((product: any) => product.subcategory),
            []
          )
        : [],
    lineage:
      reduceAndSortFilterData(
        productData.map((product: any) => product.lineage),
        []
      ) || [],
    // In bulk edit only unpublished/invalid products are in scope, so restrict
    // the Status filter options to those to keep the bulk-edit scope intact.
    status: isBulkEditTable
      ? ['Unpublished', 'Invalid data']
      : ['Publishing', 'Unpublished', 'Invalid data', 'Archived'],
    custom_rows: filterData?.filters?.custom_rows || [],
    content_type: ['Edited', 'Jane defined', 'Store defined'],
    ...(tableType === 'published' && { has_hidden_weights: [] as string[] }),
    ...(tableType === 'published' && { has_brand_discount: [] as string[] }),
  };

  type FilterKeys = keyof typeof filters;

  const isFilterKey = (k: any): k is FilterKeys => {
    return Object.keys(filters).includes(`${k}`);
  };

  const isInvalidColumnFilter = (id: string, value: any) => {
    if (id === 'status' && value?.includes('unpublished')) {
      // The default bulk-edit status scope isn't a user-applied filter, so it
      // shouldn't surface the "Clear filters" button. With the invalid products
      // flag that default is unpublished + invalid_data (narrowing to just one
      // counts as a filter); without it the default is just unpublished.
      if (isBulkEditTable && bulkEditInvalidProducts) {
        return value.includes('invalid_data');
      }
      return true;
    }

    return false;
  };

  const hasFilters = () => {
    const filtersWithValues: any[] = [];

    const cols = table.getAllColumns();
    cols.forEach((col) => {
      return (
        !isInvalidColumnFilter(col.id, col.getFilterValue()) &&
        filtersWithValues.push(col.getFilterValue())
      );
    });

    return (
      filtersWithValues.filter((filter) => {
        if (filter) {
          return filter.length > 0;
        } else {
          return null;
        }
      }).length > 0
    );
  };

  const clearFilters = () => {
    const cols = table.getAllColumns();
    cols.forEach((col) => {
      // In bulk edit, reset the status filter to the default scope rather than
      // wiping it, so clearing returns to the editable list (unpublished +
      // invalid_data) instead of showing every status.
      if (isBulkEditTable && bulkEditInvalidProducts && col.id === 'status') {
        col.setFilterValue(['unpublished', 'invalid_data']);
      } else {
        col.setFilterValue('');
      }
    });
  };

  const showClarification = useFlag(FLAGS.c3AliasClarification);
  const hasClarification =
    showClarification &&
    tableType === 'unpublished' &&
    !isBulkEditTable &&
    (productData?.some((p: any) => !!p.clarification_type) ?? false);

  return (
    <>
      <Flex justifyContent="space-between" mx={64}>
        <Flex>
          {table.getHeaderGroups().map((headerGroup) => (
            <Flex key={headerGroup.id} flexWrap="wrap" gap={16}>
              {headerGroup.headers.map((header) => {
                const key = header.id?.toString() || '';
                const isKeyAString = typeof key === 'string';
                if (!isKeyAString || !isFilterKey(key)) {
                  return;
                }

                return (
                  header.column.getCanFilter() && (
                    <TableFilter
                      column={header.column}
                      data={filters[key]}
                      key={header.column.columnDef.footer?.toString() || key}
                    />
                  )
                );
              })}
              {hasFilters() ? (
                <Button
                  key="clearfilters"
                  label="Clear filters"
                  onClick={clearFilters}
                  variant="minimal"
                />
              ) : null}
            </Flex>
          ))}
        </Flex>
        {isBulkEditTable ? (
          <Flex alignItems="center">
            <div className={styles['linkWrapper']}>
              <Link
                onClick={() => {
                  table.setRowSelection(allIds);
                  track({
                    event: EventNames.ClickedButton,
                    button_label: 'Select all',
                    modal_name: `${tableType} bulk edit`,
                  });
                }}
              >
                Select all
              </Link>
            </div>
            <div className={styles['linkWrapper']}>
              <Link
                onClick={() => {
                  table.setRowSelection({});
                  track({
                    event: EventNames.ClickedButton,
                    button_label: 'Select none',
                    modal_name: `${tableType} bulk edit`,
                  });
                }}
              >
                Select none
              </Link>
            </div>
          </Flex>
        ) : null}
      </Flex>
      {hasClarification && (
        <Box px={64} py={24}>
          <Banner
            full
            variant="error"
            icon={<AlertIcon />}
            label="Some products may not be mapped due to missing information"
            actions={
              <Link href={CLARIFICATION_LEARN_MORE_URL}>Learn more</Link>
            }
          />
        </Box>
      )}
      <div className={styles['tableWrapper']}>
        {!isFetching && Object.keys(allIds).length === 0 ? (
          <Box mt={24}>
            <EmptyState variant="noProductsFound" />
          </Box>
        ) : (
          <>
            <Table freezeFirstColumn>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Table.HeaderCell key={header.id}>
                        {header.isPlaceholder ? null : (
                          <Box
                            minWidth={
                              header.column.columnDef.footer === 'Content'
                                ? '150px'
                                : header.column.columnDef.id === 'name'
                                  ? '400px'
                                  : header.column.columnDef.id ===
                                      'clarification'
                                    ? '300px'
                                    : '100px'
                            }
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </Box>
                        )}
                      </Table.HeaderCell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Head>

              <Table.Body>
                {isFetching
                  ? Array.from({ length: 10 }, (_, i) => (
                      <LoadingRow key={i} index={i} table={table} />
                    ))
                  : table.getRowModel().rows.map((row) => (
                      <StyledRow
                        key={row.id}
                        onClick={(e) => handleRowClick(row.original, e)}
                        useDefaultCursor={!canEditProducts}
                      >
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <Table.Cell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Table.Cell>
                          );
                        })}
                      </StyledRow>
                    ))}
              </Table.Body>

              {!isFetching ? (
                <>
                  <Table.Body
                    id="loading-trigger"
                    ref={loadNextRef}
                    // Starts loading earlier than the bottom so user will not get stuck while scrolling
                    style={{
                      position: 'absolute',
                      width: '100%',
                      marginTop: '-150vh',
                    }}
                  />
                  {hasNextPage && (
                    <Table.Body style={{ marginTop: '150vh' }}>
                      <LoadingRow table={table} />
                    </Table.Body>
                  )}
                </>
              ) : (
                <></>
              )}
            </Table>
          </>
        )}
      </div>
    </>
  );
};
